import {
  BaseReminders,
  DayFrequency,
  Reminders,
  ReminderFrequency,
  ReminderSubType,
  ReminderType,
} from "@/types/reminders";
import { TFunction } from "i18next";
import MDateInputField from "@/components/ui/molecules/MDateInputField";
import { addDays, addYears, subDays } from "date-fns";
import { DAYS_OF_WEEK, DEFAULT_YEARS_AHEAD } from "@/utils/constants";
import MTimeInputField from "@/components/ui/atoms/MTimeInputField";
import MTextArea from "@/components/ui/atoms/MTextArea";
import styles from "./reminder.module.css";
import MInputField from "@/components/ui/atoms/MInputField";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import MHeader from "@/components/ui/organisms/MHeader";
import headerStyles from "@/components/ui/organisms/MHeader.module.css";
import { MemberInfo } from "@/types/member";
import {
  getReminderOptions,
  reminderOptions,
  secondsToTimeFormat,
} from "@/utils/reminder-utils";
import { IconBell, IconCaretDownFilled, IconClock } from "@tabler/icons-react";
import { MSelectCard } from "@/components/ui/atoms/MSelectCard";
import MSpacer from "@/components/ui/atoms/MSpacer";
import React from "react";
import {
  formatReminderDateWithTimezone,
  formatTimeWithTimezone,
  toLocalDateString,
} from "@/utils/date-utils";

export enum ReminderInputStage {
  START,
  FREQUENCY_DETAILS,
  FREQUENCY,
  DETAILS,
  REMINDER_DETAILS,
  DAY_FREQUENCY,
  ADD,
  DOSAGE_DETAILS,
  REMINDER_SCHEDULE,
  DAY_INPUT,
}

export const setDaysValue = (selectedDays: string[], t: TFunction): string => {
  return selectedDays
    .map((day) => t(`reminders.weekDisplay.${day}`))
    .join(", ");
};

export const frequencyToDisplay = (
  frequency: ReminderFrequency,
  selectedDays: string[],
  frequencyValue: number,
  t: TFunction
): string => {
  if (frequency === ReminderFrequency.SPECIFIC_DAYS) {
    return setDaysValue(selectedDays, t);
  } else if (frequency === ReminderFrequency.DAY_FREQUENCY) {
    return t("reminders.frequencyDaysLabel", {
      count: frequencyValue,
      days: frequencyValue,
    });
  }
  return t(`reminders.frequency.${frequency}`);
};

export const getFrequencyText = (
  frequency: ReminderFrequency | null,
  selectedDays: string[],
  frequencyValue: number,
  t: TFunction
): string | null => {
  if (!frequency) return null;

  switch (frequency) {
    case ReminderFrequency.ONLY_ONCE:
      return t("reminders.frequency.ONLY_ONCE");
    case ReminderFrequency.EVERY_DAY:
      return t("reminders.frequency.EVERY_DAY");
    case ReminderFrequency.EVERY_OTHER_DAY:
      return t("reminders.frequency.EVERY_OTHER_DAY");
    case ReminderFrequency.SPECIFIC_DAYS: {
      if (selectedDays.length === 0) return null;
      // Use abbreviated day names from weekDisplay
      const dayNames = selectedDays
        .map((day) => t(`reminders.weekDisplay.${day}`))
        .join(", ");
      return dayNames;
    }
    case ReminderFrequency.DAY_FREQUENCY:
      return t("reminders.frequency.DAY_FREQUENCY_DISPLAY", {
        count: frequencyValue,
      });
    default:
      return null;
  }
};

export const getMedicationFrequencyText = (
  frequency: ReminderFrequency | null,
  selectedDays: string[],
  frequencyValue: number | null,
  dayFrequency: DayFrequency | null,
  t: TFunction
): string => {
  if (!frequency) return "";

  const dayFreqText = dayFrequency
    ? t(`reminders.dayFrequency.${dayFrequency}`)
    : "";

  switch (frequency) {
    case ReminderFrequency.EVERY_DAY:
      return dayFreqText ? `Every day - ${dayFreqText}` : "Every day";

    case ReminderFrequency.SPECIFIC_DAYS: {
      if (selectedDays.length === 0) return "";
      const dayNames = selectedDays
        .map((day) => t(`reminders.weekDisplay.${day}`))
        .join(", ");
      return dayFreqText ? `${dayNames} - ${dayFreqText}` : dayNames;
    }

    case ReminderFrequency.DAY_FREQUENCY: {
      if (!frequencyValue) return "";
      const daysText = `Every ${frequencyValue} days`;
      return dayFreqText ? `${daysText} - ${dayFreqText}` : daysText;
    }

    case ReminderFrequency.EVERY_OTHER_DAY:
      return dayFreqText
        ? `Every other day - ${dayFreqText}`
        : "Every other day";

    default:
      return "";
  }
};

export const paintHeader = (
  memberInfo: MemberInfo,
  module: string,
  addSpacer: boolean = true,
  useParentGradient: boolean = false
) => {
  return (
    <>
      <MHeader
        module={module}
        memberInfo={memberInfo}
        useParentGradient={useParentGradient}
        className={headerStyles.stickyBelowTopBar}
      />
      {addSpacer && <MSpacer />}
    </>
  );
};

export const paintEventDateField = (
  eventDate: Date | null,
  setEventDate: (date: Date | null) => void,
  t: TFunction,
  labelKey?: string,
  isSummaryDisplay: boolean = false
) => {
  return (
    <MDateInputField
      label={t(`reminders.${labelKey ? labelKey + "." : ""}eventDateLabel`)}
      placeholder={t("common.notAnswered")}
      value={eventDate}
      variant={isSummaryDisplay ? "SummaryDisplay" : "Underlined"}
      mandatory={true}
      onDateChange={setEventDate}
      datePickerTitle={t("common.selectDate")}
      minDate={new Date()}
      maxDate={addYears(new Date(), DEFAULT_YEARS_AHEAD)}
    />
  );
};

export const paintEventTimeField = (
  eventDate: Date | null,
  eventTime: string | null,
  setEventTime: (time: string | null) => void,
  t: TFunction,
  labelKey?: string,
  isSummaryDisplay: boolean = false
) => {
  return (
    <MTimeInputField
      label={t(`reminders.${labelKey ? labelKey + "." : ""}eventTimeLabel`)}
      placeholder={t("common.notAnswered")}
      value={eventTime}
      variant={isSummaryDisplay ? "SummaryDisplay" : "Underlined"}
      timePickerTitle={t("common.selectTime")}
      showLabel={true}
      format={12}
      onChange={(e) => setEventTime(e)}
      disabled={eventDate === null}
    />
  );
};

export const paintReminderDaysField = (
  reminderDays: number | null,
  setReminderDays: (days: number | null) => void,
  reminderDrawerOpen: boolean,
  setReminderDrawerOpen: (open: boolean) => void,
  eventDate: Date | null,
  setReminderDate: (date: Date | null) => void,
  t: TFunction,
  labelKey?: string
) => {
  return (
    <>
      <div
        className="cursor-pointer w-full"
        onClick={() => setReminderDrawerOpen(true)}
      >
        <MInputField
          label={t(
            `reminders.${labelKey ? labelKey + "." : ""}reminderDaysLabel`
          )}
          placeholder={t("common.notAnswered")}
          value={
            reminderOptions(t, labelKey).find(
              (option) => option.value === reminderDays
            )?.label || ""
          }
          onChange={() => {}}
          icon={<IconCaretDownFilled />}
          showIcon={true}
          disabled={false}
        />
      </div>
      <MDropDownDrawer
        open={reminderDrawerOpen}
        onClose={() => setReminderDrawerOpen(false)}
        title={t(
          `reminders.${labelKey ? labelKey + "." : ""}reminderDaysLabel`
        )}
        items={getReminderOptions(eventDate, t, labelKey)}
        onSelect={(value) => {
          const reminderDays = Number(value);
          setReminderDays(reminderDays);
          setReminderDate(subDays(eventDate, reminderDays));
        }}
      />
    </>
  );
};

export const paintReminderDateField = (
  reminderDate: Date | null,
  setReminderDate: (date: Date | null) => void,
  maxDate: Date | null,
  t: TFunction,
  labelKey?: string,
  showLabel: boolean = true,
  isSummaryDisplay: boolean = false
) => {
  return (
    <MDateInputField
      label={t(`reminders.${labelKey ? labelKey + "." : ""}reminderDateLabel`)}
      placeholder={t("common.notAnswered")}
      value={reminderDate}
      mandatory={true}
      showLabel={showLabel}
      variant={isSummaryDisplay ? "SummaryDisplay" : "Underlined"}
      onDateChange={setReminderDate}
      datePickerTitle={t("common.selectDate")}
      minDate={new Date()}
      maxDate={maxDate || addYears(new Date(), DEFAULT_YEARS_AHEAD)}
    />
  );
};

export const paintReminderEndDateField = (
  reminderEndDate: Date | null,
  setReminderEndDate: (date: Date | null) => void,
  maxDate: Date | null,
  t: TFunction,
  labelKey?: string,
  showLabel: boolean = true,
  isSummaryDisplay: boolean = false
) => {
  return (
    <MDateInputField
      label={t(
        `reminders.${labelKey ? labelKey + "." : ""}reminderEndDateLabel`
      )}
      placeholder={t("common.notAnswered")}
      value={reminderEndDate}
      showLabel={showLabel}
      variant={isSummaryDisplay ? "SummaryDisplay" : "Underlined"}
      onDateChange={setReminderEndDate}
      datePickerTitle={t("common.selectDate")}
      minDate={addDays(new Date(), 1)}
      maxDate={maxDate || addYears(new Date(), DEFAULT_YEARS_AHEAD)}
    />
  );
};

export const paintReminderTimeField = (
  reminderTime: string | null,
  setReminderTime: (time: string | null) => void,
  reminderDate: Date | null,
  t: TFunction,
  labelKey?: string,
  error?: string,
  isSummaryDisplay: boolean = false
) => {
  return (
    <MTimeInputField
      label={t(`reminders.${labelKey ? labelKey + "." : ""}reminderTimeLabel`)}
      placeholder={t("common.notAnswered")}
      value={reminderTime}
      variant={isSummaryDisplay ? "SummaryDisplay" : "Underlined"}
      timePickerTitle={t("common.selectTime")}
      showLabel={true}
      format={12}
      state={error ? "Error" : "Default"}
      stateMessage={error || ""}
      onChange={(e) => setReminderTime(e)}
      disabled={reminderDate === null}
    />
  );
};

// Component for reminder notes field with edit icon
const ReminderNotesField: React.FC<{
  notes: string | null;
  setNotes: (notes: string | null) => void;
  t: TFunction;
  key?: string;
  isSummaryDisplay?: boolean;
  textAreaClassName?: string;
}> = ({
  notes,
  setNotes,
  t,
  key,
  isSummaryDisplay = false,
  textAreaClassName,
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const handleEditClick = () => {
    textareaRef.current?.focus();
  };

  return (
    <div className="w-full">
      <MTextArea
        ref={textareaRef}
        label={t(
          isSummaryDisplay
            ? "reminders.detailsLabel"
            : "reminders.addDetailsLabel"
        )}
        placeholder={t(
          `reminders.${key ? key + "." : ""}addDetailsPlaceholder`
        )}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        className={textAreaClassName || styles.textArea}
        maxLength={250}
        variant={isSummaryDisplay ? "SummaryDisplay" : undefined}
        style={{ width: "100%" }}
        showEditIcon={isSummaryDisplay}
        onEditIconClick={handleEditClick}
      />
    </div>
  );
};

export const paintReminderNotesField = (
  notes: string | null,
  setNotes: (notes: string | null) => void,
  t: TFunction,
  key?: string,
  isSummaryDisplay: boolean = false,
  textAreaClassName?: string
) => {
  return (
    <ReminderNotesField
      notes={notes}
      setNotes={setNotes}
      t={t}
      key={key}
      isSummaryDisplay={isSummaryDisplay}
      textAreaClassName={textAreaClassName}
    />
  );
};

export const paintEventForField = (
  eventFor: string | null,
  setEventFor: (eventFor: string | null) => void,
  t: TFunction,
  type?: string
) => {
  return (
    <MInputField
      label={t(`reminders.${type ? type + "." : ""}eventForLabel`)}
      placeholder={t("common.notAnswered")}
      value={eventFor}
      onChange={(e) => setEventFor(e.target.value)}
      mandatory={true}
      type="text"
    />
  );
};

export const displayEventForField = (
  eventFor: string,
  t: TFunction,
  type?: string,
  variant: "Underlined" | "SummaryDisplay" = "Underlined"
) => {
  return (
    <MInputField
      label={t(`reminders.${type ? type + "." : ""}eventForLabel`)}
      value={eventFor}
      disabled={true}
      type="text"
      variant={variant}
    />
  );
};

export const paintFrequencySelectField = (
  frequency: ReminderFrequency,
  onSelect: (frequency: ReminderFrequency) => void,
  t: TFunction,
  frequencies: ReminderFrequency[] = Object.values(ReminderFrequency)
) => {
  return (
    <div className={styles.frequencyContainer}>
      {frequencies.map((option) => (
        <MSelectCard
          key={option}
          text={t(`reminders.frequency.${option}`)}
          selected={frequency === option}
          onClick={() => onSelect(option)}
          centerAlign={true}
          className="w-full"
        />
      ))}
    </div>
  );
};

export const paintDropDownFrequencySelectField = (
  frequency: ReminderFrequency,
  onSelect: (frequency: ReminderFrequency) => void,
  showFrequencyDrawer: boolean,
  setShowFrequencyDrawer: (show: boolean) => void,
  t: TFunction,
  labelKey: string = "medication",
  frequencies: ReminderFrequency[] = Object.values(ReminderFrequency)
) => {
  return (
    <>
      <MInputField
        label={t(`reminders.${labelKey}.frequencyLabel`)}
        placeholder={t("common.notAnswered")}
        value={frequency ? t(`reminders.frequency.${frequency}`) : null}
        onClick={() => setShowFrequencyDrawer(true)}
        type="text"
        icon={<IconCaretDownFilled />}
        showIcon={true}
        mandatory={true}
      />
      <MDropDownDrawer
        open={showFrequencyDrawer}
        onClose={() => {
          setShowFrequencyDrawer(false);
        }}
        title={t("reminders.frequencySelectLabel")}
        initialValue={frequency}
        items={frequencies.map((option) => ({
          label: t(`reminders.frequency.${option}`),
          value: option,
        }))}
        onSelect={(value) => onSelect(value as ReminderFrequency)}
      />
    </>
  );
};

export const paintDayFrequencyField = (
  dayFrequency: DayFrequency,
  onSelect: (dayFrequency: DayFrequency) => void,
  showDayFrequencyDrawer: boolean,
  setShowDayFrequencyDrawer: (show: boolean) => void,
  t: TFunction,
  labelKey: string = "medication"
) => {
  return (
    <>
      <MInputField
        label={t(`reminders.${labelKey}.dayFrequencyLabel`)}
        placeholder={t("common.notAnswered")}
        value={
          dayFrequency ? t(`reminders.dayFrequency.${dayFrequency}`) : null
        }
        onClick={() => setShowDayFrequencyDrawer(true)}
        type="text"
        icon={<IconCaretDownFilled />}
        showIcon={true}
        mandatory={true}
      />
      <MDropDownDrawer
        open={showDayFrequencyDrawer}
        onClose={() => {
          setShowDayFrequencyDrawer(false);
        }}
        title={t("reminders.medication.dayFrequencySelectLabel")}
        initialValue={dayFrequency}
        items={Object.values(DayFrequency).map((option) => ({
          label: t(`reminders.dayFrequency.${option}`),
          value: option,
        }))}
        onSelect={(value) => onSelect(value as DayFrequency)}
      />
    </>
  );
};

export const displayDayFrequencyField = (
  dayFrequency: DayFrequency,
  t: TFunction,
  variant: "Underlined" | "SummaryDisplay" = "Underlined"
) => {
  return (
    <MInputField
      label={t(`reminders.medication.add.dayFrequencyLabel`)}
      value={t(`reminders.dayFrequency.${dayFrequency}`)}
      disabled={true}
      type="text"
      variant={variant}
    />
  );
};

export const displayFrequencyField = (
  frequency: ReminderFrequency,
  t: TFunction,
  variant: "Underlined" | "SummaryDisplay" = "Underlined"
) => {
  return (
    <MInputField
      label={t(`reminders.medication.add.frequencyLabel`)}
      value={t(`reminders.frequency.${frequency}`)}
      disabled={true}
      type="text"
      variant={variant}
    />
  );
};

export const paintDosageInput = (
  firstDosage: string,
  setFirstDosage: (firstDosage: string) => void,
  t: TFunction,
  showLabel: boolean = true,
  labelKey?: string,
  mandatory: boolean = true,
  isSummaryDisplay: boolean = false
) => {
  return (
    <MTimeInputField
      label={t(
        `reminders.medication.dosageLabel${labelKey ? "." + labelKey : ""}`
      )}
      placeholder={t("common.notAnswered")}
      value={firstDosage}
      variant={isSummaryDisplay ? "SummaryDisplay" : "Underlined"}
      timePickerTitle={t("common.selectTime")}
      showLabel={showLabel}
      mandatory={mandatory}
      format={12}
      onChange={(e) => setFirstDosage(e)}
    />
  );
};

export const paintFrequencySpecificDaysField = (
  selectedDays: string[],
  setShowSetDays: (showSetDays: boolean) => void,
  showSetDays: boolean,
  handleSetDaysSelect: (options: string[]) => void,
  t: TFunction,
  labelKey?: string
) => {
  return (
    <>
      <MInputField
        label={t(
          `reminders.${
            labelKey ? labelKey + "." : ""
          }frequencySpecificDaysLabel`
        )}
        placeholder={t("common.notAnswered")}
        value={setDaysValue(selectedDays, t)}
        onClick={() => setShowSetDays(true)}
        type="text"
        icon={<IconCaretDownFilled />}
        showIcon={true}
        mandatory={true}
      />
      <MDropDownDrawer
        title={t("reminders.setDaysLabel")}
        open={showSetDays}
        onClose={() => setShowSetDays(false)}
        items={Object.keys(DAYS_OF_WEEK).map((key) => ({
          value: key,
          label: t(`reminders.daysOfWeek.${key}`),
        }))}
        onSelect={(options) =>
          handleSetDaysSelect(Array.isArray(options) ? options : [options])
        }
        variant="multi"
        selectedValues={selectedDays}
      />
    </>
  );
};

export const paintFrequencyDaysField = (
  frequencyValue: number,
  setFrequencyValue: (frequencyValue: number) => void,
  t: TFunction,
  labelKey?: string
) => {
  return (
    <div>
      <label className="profile-input-label">
        {t(`reminders${labelKey ? "." + labelKey : ""}.frequencyDaysLabel`)}
      </label>
      <MInputField
        type="number"
        showLabel={false}
        value={frequencyValue?.toString()}
        siUnit={t("common.days")}
        showSIUnit
        onChange={(e) => setFrequencyValue(Number(e.target.value))}
        className="w-[156.5px]"
      />
    </div>
  );
};

export const paintFrequencyDisplayField = (
  frequency: ReminderFrequency,
  selectedDays: string[],
  frequencyValue: number,
  t: TFunction,
  labelKey?: string,
  variant: "Underlined" | "SummaryDisplay" = "Underlined"
) => {
  return (
    <MInputField
      label={t(
        `reminders.appointment${labelKey ? "." + labelKey : ""}.frequencyLabel`
      )}
      placeholder={t("common.notAnswered")}
      value={frequencyToDisplay(frequency, selectedDays, frequencyValue, t)}
      disabled={true}
      type="text"
      variant={variant}
    />
  );
};

// Helper functions for RemindersListing component

export function getSecondaryText(_reminder: BaseReminders): string {
  return "";
}

export function getEmphasizedText(
  reminder: Reminders,
  t: TFunction,
  petCareData?: { frequencyType?: string; frequency?: number }
): string {
  const eventOrStart = reminder.eventDate ?? reminder.startDate;
  const eventDateStr = formatReminderDateWithTimezone(
    eventOrStart,
    reminder.timezone
  );
  const eventTimeStr = formatTimeWithTimezone(eventOrStart, reminder.timezone);
  const endDateStr =
    reminder.endDate &&
    formatReminderDateWithTimezone(reminder.endDate, reminder.timezone);

  switch (reminder.type) {
    case ReminderType.MEDICATION: {
      let freq: string;
      switch (reminder.frequency) {
        case ReminderFrequency.SPECIFIC_DAYS:
          freq =
            reminder.selectedDays
              ?.map((d) => t(`reminders.weekDisplay.${d}`))
              .join(", ") ?? "";
          break;
        case ReminderFrequency.DAY_FREQUENCY:
          freq = t(`reminders.frequencyDaysLabel`, {
            count: reminder.frequencyValue ?? 0,
            days: reminder.frequencyValue ?? 0,
          });
          break;
        default:
          freq = t(`reminders.frequency.${reminder.frequency}`);
      }
      if (endDateStr && reminder.frequency !== ReminderFrequency.EVERY_DAY) {
        return (
          freq + t("reminders.emphasizedText.tillDate", { date: endDateStr })
        );
      }
      return freq;
    }
    case ReminderType.APPOINTMENT: {
      // Time is OPTIONAL on appointments. When the user doesn't set one
      // we must not synthesise "12:00 AM" from the startDate fallback —
      // render the date alone via the date-only template instead.
      const hasEventTime = Boolean(reminder.eventTime);

      // For therapy sessions with frequency
      if (reminder.subType === ReminderSubType.THERAPY && reminder.frequency) {
        switch (reminder.frequency) {
          case ReminderFrequency.ONLY_ONCE:
            return hasEventTime
              ? t("reminders.emphasizedText.appointmentOn", {
                  date: eventDateStr,
                  time: eventTimeStr,
                })
              : t("reminders.emphasizedText.appointmentOnDateOnly", {
                  date: eventDateStr,
                });
          case ReminderFrequency.EVERY_DAY:
            return t(`reminders.frequency.EVERY_DAY`);
          case ReminderFrequency.EVERY_OTHER_DAY:
            return t(`reminders.frequency.EVERY_OTHER_DAY`);
          case ReminderFrequency.SPECIFIC_DAYS:
            if (reminder.selectedDays?.length && endDateStr) {
              const days = reminder.selectedDays
                .map((d) => t(`reminders.weekDisplay.${d}`))
                .join(", ");
              return t("reminders.emphasizedText.daysTill", {
                days,
                date: endDateStr,
              });
            }
            break;
          case ReminderFrequency.DAY_FREQUENCY:
            return t(`reminders.frequencyDaysLabel`, {
              count: reminder.frequencyValue ?? 0,
              days: reminder.frequencyValue ?? 0,
            });
        }
      }

      return hasEventTime
        ? t("reminders.emphasizedText.appointmentOn", {
            date: eventDateStr,
            time: eventTimeStr,
          })
        : t("reminders.emphasizedText.appointmentOnDateOnly", {
            date: eventDateStr,
          });
    }
    case ReminderType.INSURANCE:
      return eventDateStr
        ? t("reminders.emphasizedText.expiryOn", { date: eventDateStr })
        : t("reminders.emphasizedText.default", {
            date: eventDateStr,
            time: eventTimeStr,
          });
    case ReminderType.VACCINATION:
      return t("reminders.emphasizedText.dueOn", {
        date: eventDateStr,
        time: eventTimeStr,
      });
    case ReminderType.PET_CARE: {
      const frequencyType =
        reminder.reminderAttrs?.frequencyType || petCareData?.frequencyType;
      const customFrequency =
        reminder.reminderAttrs?.frequency || petCareData?.frequency;

      if (frequencyType) {
        switch (frequencyType) {
          case "monthly":
            return t("petcare.label.frequency_monthly");
          case "quaterly":
            return t("petcare.label.frequency_quarterly");
          case "bi-weekly":
            return t("petcare.label.frequency_bi_weekly");
          case "three-months":
            return t("petcare.label.frequency_three_months");
          case "other":
            if (customFrequency) {
              return t("reminders.frequencyDaysLabel", {
                count: customFrequency,
                days: customFrequency,
              });
            }
            break;
        }
      }

      return t("reminders.emphasizedText.dueOn", {
        date: eventDateStr,
        time: eventTimeStr,
      });
    }
    default: {
      return t("reminders.emphasizedText.default", {
        date: eventDateStr,
        time: eventTimeStr,
      });
    }
  }
}

export function getTertiaryText(reminder: BaseReminders): string {
  const r = reminder as Reminders;
  if (
    reminder.type === ReminderType.MEDICATION &&
    r.dayFrequencyValue?.length
  ) {
    return r.dayFrequencyValue.map(secondsToTimeFormat).join(" • ");
  }

  const reminderDate = new Date(reminder.startDate);
  const eventOrStart = reminder.eventDate ?? reminder.startDate;
  const eventDate = new Date(eventOrStart);
  const sameDay =
    toLocalDateString(reminderDate) === toLocalDateString(eventDate);

  // The bottom row (bell icon) is the REMINDER fire time, not the
  // appointment time. Use startDate's time component — this is when the
  // notification actually fires. eventTime (the appointment time) is
  // already rendered on the top emphasised row by getEmphasizedText.
  const timeStr = formatTimeWithTimezone(reminder.startDate, reminder.timezone);

  if (reminder.type === ReminderType.PET_CARE) {
    const dateStr = formatReminderDateWithTimezone(
      reminder.startDate,
      reminder.timezone
    );
    return `${dateStr} at ${timeStr}`;
  }

  if (sameDay && reminder.eventDate) {
    return timeStr;
  }
  const dateStr = formatReminderDateWithTimezone(
    reminder.startDate,
    reminder.timezone
  );
  return `${dateStr} at ${timeStr}`;
}

export function getTertiaryIcon(reminder: BaseReminders): React.ReactNode {
  const size = 14;

  // Show bell icon for appointment and vaccination reminders
  if (
    reminder.type === ReminderType.APPOINTMENT ||
    reminder.type === ReminderType.VACCINATION
  ) {
    return <IconBell size={size} />;
  }

  return <IconClock size={size} />;
}
